import React from "react";
import siteConfig from "../../config/siteConfig";
import { LinkInfo } from "../../models/shared/LinkInfo";
import { SanityImageSourceExt } from "../../models/shared/SanityImageSourceExt";
import { isNullOrWhiteSpace } from "../../shared/string";
import { isValidLink } from "../../utilities/links";
import Button, { ButtonSize } from "../Buttons/Button";
import PortableText, { PortableTextBody } from "../PortableText";
import SanityImage from "../SanityImage";
import BannerContainer, { BannerContainerProps } from "./BannerContainer";
import { BannerColorTheme, BannerContentPosition, getBannerColorTheme } from "./Common";

export interface BannerTextAndImageProps extends BannerContainerProps {
  title?: string;
  subtitle?: string;
  image?: SanityImageSourceExt;
  portableTextContent?: PortableTextBody;
  _rawPortableTextContent?: PortableTextBody;
  buttonLink?: LinkInfo;
  textPosition?: BannerContentPosition;
}

const BannerImageForMarketSite = ({ image }: { image: SanityImageSourceExt }) => (
  <div className={"banner-item flex justify-end items-center md:mr-24"}>
    <SanityImage image={image} loading="lazy" width={650} height={452} />
  </div>
);

const BannerText = (props: BannerTextAndImageProps) => {
  const {
    title,
    subtitle,
    colorTheme,
    portableTextContent,
    _rawPortableTextContent,
    buttonLink,
    image
  } = props;

  const { titleColor, subtitleColor, textColor, hrColor, buttonColor } =
    getBannerColorTheme(colorTheme ?? BannerColorTheme.Default);

  let classNames = "flex flex-col pb-8 lg:pt-8";
  if (siteConfig.isGroup === false && !image) {
    // If no image, center text content for market site
    classNames += " max-w-4xl mx-auto";
  } else {
    classNames += " banner-item md:mr-24";
  }

  return (
    <div className={classNames}>
      {!isNullOrWhiteSpace(title) && (
        <h2 className={`${titleColor} mb-2 text-2xl lg:text-5xl`}>{title}</h2>
      )}
      {!isNullOrWhiteSpace(subtitle) && (
        <h3 className={`text-xl ${subtitleColor} uppercase font-bold mb-2 lg:mb-0`}>
          {subtitle}
        </h3>
      )}

      {siteConfig.isGroup &&
        (!isNullOrWhiteSpace(subtitle) || !isNullOrWhiteSpace(title)) && (
          <hr className={`h-divider-thick w-16 ${hrColor} mt-4 mb-8 lg:mb-10`} />
        )}

      {(_rawPortableTextContent || portableTextContent) && (
        <PortableText
          _rawBody={_rawPortableTextContent ?? portableTextContent}
          addSections={false}
          elementClasses={{
            p: `text-xl ${textColor} lg:text-2xl`,
            li: `${textColor} mb-3 last:mb-0 text-xl font-bold`
          }}
        />
      )}

      {isValidLink(buttonLink) && (
        <Button
          className="flex justify-center md:self-start mt-16 mb-6"
          color={buttonColor}
          size={ButtonSize.Normal}
          type="link"
          {...buttonLink}
        >
          {buttonLink.title}
        </Button>
      )}
    </div>
  );
};

const BannerTextAndImage = (props: BannerTextAndImageProps) => {
  const { image, textPosition } = props;

  let negativeMargin = "";
  if (
    image ||
    textPosition === BannerContentPosition.Left ||
    textPosition === BannerContentPosition.Right
  ) {
    negativeMargin = "md:-mr-24";
  }

  return (
    <BannerContainer {...props}>
      <div
        className={"mx-auto overflow-hidden"}
        style={{ maxWidth: "calc(1420px + 6rem)" }}
      >
        <div
          className={`
                        flex flex-col justify-between max-w-full 
                        md:flex-row
                        ${negativeMargin} 
                        ${
                          textPosition !== BannerContentPosition.Left
                            ? "md:flex-row-reverse"
                            : ""
                        }
                    `}
        >
          <BannerText {...props} />
          {image && <BannerImageForMarketSite image={image} />}
        </div>
      </div>
    </BannerContainer>
  );
};

export default BannerTextAndImage;

